import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileCategorySearch, setMobileCategorySearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredCategories = mobileCategorySearch.trim()
    ? categories.filter((cat) =>
        cat.name.toLowerCase().includes(mobileCategorySearch.toLowerCase())
      )
    : categories;

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled || mobileMenuOpen
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            className="flex items-center gap-2 group"
          >
            <span className="w-8 h-8 rounded-lg bg-accent-500 flex items-center justify-center">
              <i className="ri-database-2-line text-background-50 text-lg"></i>
            </span>
            <span className="font-heading font-bold text-lg text-foreground-900 hidden sm:block">
              Superbuy<span className="text-accent-600">Spreadsheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              Home
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                  dropdownOpen
                    ? 'bg-accent-100 text-accent-700'
                    : 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
                }`}
              >
                Categories
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-64 bg-background-50 rounded-xl border border-background-200/80 py-2 overflow-hidden animate-[fadeIn_0.15s_ease-out]">
                  <div className="max-h-[420px] overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.slug}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-50 hover:text-accent-700 transition-colors cursor-pointer"
                      >
                        <span className="w-7 h-7 rounded-md bg-background-100 flex items-center justify-center flex-shrink-0">
                          <i className={`${cat.icon} text-foreground-500 text-sm`}></i>
                        </span>
                        <span className="font-medium whitespace-nowrap">{cat.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/guide/superbuy-spreadsheet-101"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              Guide
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Superbuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 bg-accent-500 text-background-50 text-sm font-medium rounded-full hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50/95 backdrop-blur-md max-h-[calc(100dvh-4rem)] overflow-y-auto pb-5 border-t border-background-200/60">
            <div className="py-3 space-y-1">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-xl"
              >
                <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-home-4-line text-primary-600 text-base"></i>
                </span>
                <span>Home</span>
              </Link>

              <div className="px-3 pt-2 pb-1">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-grid-line text-accent-600 text-base"></i>
                    </span>
                    <span className="text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                      Categories
                    </span>
                  </div>
                  <span className="text-xs font-medium text-foreground-400 bg-background-100 px-2 py-0.5 rounded-full">
                    {categories.length}
                  </span>
                </div>

                <div className="relative mb-3">
                  <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                  <input
                    type="text"
                    placeholder="Filter categories..."
                    value={mobileCategorySearch}
                    onChange={(e) => setMobileCategorySearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 text-sm bg-background-50 border border-background-200/60 rounded-xl text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:border-accent-300 transition-colors"
                  />
                  {mobileCategorySearch && (
                    <button
                      onClick={() => setMobileCategorySearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-background-200 flex items-center justify-center"
                    >
                      <i className="ri-close-line text-xs text-foreground-500"></i>
                    </button>
                  )}
                </div>

                {filteredCategories.length === 0 ? (
                  <div className="text-center py-6">
                    <i className="ri-search-line text-2xl text-foreground-300 block mb-2"></i>
                    <p className="text-xs text-foreground-400">No categories match &quot;{mobileCategorySearch}&quot;</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-1.5 pr-1">
                    {filteredCategories.map((cat) => (
                      <a
                        key={cat.slug}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setMobileCategorySearch('');
                        }}
                        className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-foreground-600 hover:bg-accent-50 hover:text-accent-700 rounded-xl cursor-pointer transition-colors"
                      >
                        <i className={`${cat.icon} text-sm text-foreground-400 flex-shrink-0`}></i>
                        <span className="truncate text-xs font-medium">{cat.name}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/guide/superbuy-spreadsheet-101"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-xl"
              >
                <span className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-book-open-line text-secondary-600 text-base"></i>
                </span>
                <span>Beginner&apos;s Guide</span>
              </Link>
            </div>

            <div className="flex flex-col gap-2 px-3 pt-3 border-t border-background-200/60">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="w-full py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-full text-center hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-arrow-right-up-line"></i>
                Superbuy Sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="w-full py-3 bg-accent-500 text-background-50 text-sm font-medium rounded-full text-center hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-shopping-bag-line"></i>
                Buy It Now
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}